import { } from 'react-redux';

import { Action } from '@reduxjs/toolkit';

import * as actionTypes from '../constants/twilio';

import { updateObject } from '../../../../shared/utils/utility';

import { PhoneGroup, TwilioAccount } from '../../models';
import { AppAction } from '../../../../store';

export type TwilioState = {
  twilioAccountSettings: TwilioAccount | null;
  twilioAccountSettingsError: any | null;
  twilioAccountSettingsLoading: boolean;

  saveTwilioAccountSettings: boolean | null;
  saveTwilioAccountSettingsError: any | null;
  saveTwilioAccountSettingsLoading: boolean;

  removeTwilioAccount: boolean | null;
  removeTwilioAccountError: any | null;
  removeTwilioAccountLoading: boolean;

  allTwilioPhones: Array<string> | null;
  allTwilioPhonesError: any | null;
  allTwilioPhonesLoading: boolean;

  twilioAssignedAgents: Array<PhoneGroup> | null;
  twilioAssignedAgentsError: any | null;
  twilioAssignedAgentsLoading: boolean;

  saveTwilioAssignedAgentsResult: boolean | null;
  saveTwilioAssignedAgentsResultError: any | null;
  saveTwilioAssignedAgentsResultLoading: boolean;
};

const initialState: TwilioState = {
  twilioAccountSettings: null,
  twilioAccountSettingsError: null,
  twilioAccountSettingsLoading: false,

  saveTwilioAccountSettings: null,
  saveTwilioAccountSettingsError: null,
  saveTwilioAccountSettingsLoading: false,

  removeTwilioAccount: null,
  removeTwilioAccountError: null,
  removeTwilioAccountLoading: false,

  allTwilioPhones: null,
  allTwilioPhonesError: null,
  allTwilioPhonesLoading: false,

  twilioAssignedAgents: null,
  twilioAssignedAgentsError: null,
  twilioAssignedAgentsLoading: false,

  saveTwilioAssignedAgentsResult: null,
  saveTwilioAssignedAgentsResultError: null,
  saveTwilioAssignedAgentsResultLoading: false,
};

const getTwilioAccountSettingsStart = (state: TwilioState, action: AppAction<TwilioAccount>) => {
  return updateObject(state, {
    twilioAccountSettings: null,
    twilioAccountSettingsError: null,
    twilioAccountSettingsLoading: true,
  });
};

const getTwilioAccountSettingsSuccess = (state: TwilioState, action: AppAction<TwilioAccount>) => {
  return updateObject(state, {
    twilioAccountSettings: action.data,
    twilioAccountSettingsError: null,
    twilioAccountSettingsLoading: false,
  });
};

const getTwilioAccountSettingsFail = (state: TwilioState, action: AppAction<TwilioAccount>) => {
  return updateObject(state, {
    twilioAccountSettings: null,
    twilioAccountSettingsError: action.error,
    twilioAccountSettingsLoading: false,
  });
};

const saveTwilioAccountSettingsStart = (state: TwilioState, action: AppAction<boolean>) => {
  return updateObject(state, {
    saveTwilioAccountSettingsError: null,
    saveTwilioAccountSettingsLoading: true,
  });
};

const saveTwilioAccountSettingsSuccess = (state: TwilioState, action: AppAction<boolean>) => {
  return updateObject(state, {
    saveTwilioAccountSettings: action.data,
    saveTwilioAccountSettingsError: null,
    saveTwilioAccountSettingsLoading: false,
  });
};

const saveTwilioAccountSettingsFail = (state: TwilioState, action: AppAction<boolean>) => {
  return updateObject(state, {
    saveTwilioAccountSettings: null,
    saveTwilioAccountSettingsError: action.error,
    saveTwilioAccountSettingsLoading: false,
  });
};

const removeTwilioAccountStart = (state: TwilioState, action: AppAction<boolean>) => {
  return updateObject(state, {
    removeTwilioAccountError: null,
    removeTwilioAccountLoading: true,
  });
};

const removeTwilioAccountSuccess = (state: TwilioState, action: AppAction<boolean>) => {
  return updateObject(state, {
    removeTwilioAccount: action.data,
    removeTwilioAccountError: null,
    removeTwilioAccountLoading: false,
  });
};

const removeTwilioAccountFail = (state: TwilioState, action: AppAction<boolean>) => {
  return updateObject(state, {
    removeTwilioAccount: null,
    removeTwilioAccountError: action.error,
    removeTwilioAccountLoading: false,
  });
};

const getAllTwilioPhonesStart = (state: TwilioState, action: AppAction<Array<string>>) => {
  return updateObject(state, {
    allTwilioPhones: null,
    allTwilioPhonesError: null,
    allTwilioPhonesLoading: true,
  });
};

const getAllTwilioPhonesSuccess = (state: TwilioState, action: AppAction<Array<string>>) => {
  return updateObject(state, {
    allTwilioPhones: action.data,
    allTwilioPhonesError: null,
    allTwilioPhonesLoading: false,
  });
};

const getAllTwilioPhonesFail = (state: TwilioState, action: AppAction<Array<string>>) => {
  return updateObject(state, {
    allTwilioPhones: null,
    allTwilioPhonesError: action.error,
    allTwilioPhonesLoading: false,
  });
};

const setAllTwilioPhones = (state: TwilioState, action: AppAction<Array<string>>) => {
  return updateObject(state, {
    allTwilioPhones: action.data,
    allTwilioPhonesError: null,
    allTwilioPhonesLoading: false,
  });
};

const getTwilioAssignedAgentsStart = (state: TwilioState, action: AppAction<Array<PhoneGroup>>) => {
  return updateObject(state, {
    twilioAssignedAgents: null,
    twilioAssignedAgentsError: null,
    twilioAssignedAgentsLoading: true,
  });
};

const getTwilioAssignedAgentsSuccess = (state: TwilioState, action: AppAction<Array<PhoneGroup>>) => {
  return updateObject(state, {
    twilioAssignedAgents: action.data,
    twilioAssignedAgentsError: null,
    twilioAssignedAgentsLoading: false,
  });
};

const getTwilioAssignedAgentsFail = (state: TwilioState, action: AppAction<Array<PhoneGroup>>) => {
  return updateObject(state, {
    twilioAssignedAgents: null,
    twilioAssignedAgentsError: action.error,
    twilioAssignedAgentsLoading: false,
  });
};

const setTwilioAssignedAgents = (state: TwilioState, action: AppAction<Array<PhoneGroup>>) => {
  return updateObject(state, {
    twilioAssignedAgents: action.data,
    twilioAssignedAgentsError: null,
    twilioAssignedAgentsLoading: false,
  });
};

const saveTwilioAssignedAgentsStart = (state: TwilioState, action: AppAction<boolean>) => {
  return updateObject(state, {
    saveTwilioAssignedAgentsResult: null,
    saveTwilioAssignedAgentsResultError: null,
    saveTwilioAssignedAgentsResultLoading: true,
  });
};

const saveTwilioAssignedAgentsSuccess = (state: TwilioState, action: AppAction<boolean>) => {
  return updateObject(state, {
    saveTwilioAssignedAgentsResult: action.data,
    saveTwilioAssignedAgentsResultError: null,
    saveTwilioAssignedAgentsResultLoading: false,
  });
};

const saveTwilioAssignedAgentsFail = (state: TwilioState, action: AppAction<boolean>) => {
  return updateObject(state, {
    saveTwilioAssignedAgentsResult: null,
    saveTwilioAssignedAgentsResultError: action.error,
    saveTwilioAssignedAgentsResultLoading: false,
  });
};

const reducer = (state = initialState, action: Action) => {
  switch (action.type) {
    case actionTypes.TWILIO_GET_TWILIO_SETTINGS_START:
      return getTwilioAccountSettingsStart(state, action as AppAction<TwilioAccount>);
    case actionTypes.TWILIO_GET_TWILIO_SETTINGS_SUCCESS:
      return getTwilioAccountSettingsSuccess(state, action as AppAction<TwilioAccount>);
    case actionTypes.TWILIO_GET_TWILIO_SETTINGS_FAIL:
      return getTwilioAccountSettingsFail(state, action as AppAction<TwilioAccount>);

    case actionTypes.TWILIO_SAVE_TWILIO_ACCOUNT_SETTINGS_START:
      return saveTwilioAccountSettingsStart(state, action as AppAction<boolean>);
    case actionTypes.TWILIO_SAVE_TWILIO_ACCOUNT_SETTINGS_SUCCESS:
      return saveTwilioAccountSettingsSuccess(state, action as AppAction<boolean>);
    case actionTypes.TWILIO_SAVE_TWILIO_ACCOUNT_SETTINGS_FAIL:
      return saveTwilioAccountSettingsFail(state, action as AppAction<boolean>);

    case actionTypes.TWILIO_REMOVE_TWILIO_ACCOUNT_START:
      return removeTwilioAccountStart(state, action as AppAction<boolean>);
    case actionTypes.TWILIO_REMOVE_TWILIO_ACCOUNT_SUCCESS:
      return removeTwilioAccountSuccess(state, action as AppAction<boolean>);
    case actionTypes.TWILIO_REMOVE_TWILIO_ACCOUNT_FAIL:
      return removeTwilioAccountFail(state, action as AppAction<boolean>);

    case actionTypes.TWILIO_GET_ALL_TWILIO_PHONES_START:
      return getAllTwilioPhonesStart(state, action as AppAction<Array<string>>);
    case actionTypes.TWILIO_GET_ALL_TWILIO_PHONES_SUCCESS:
      return getAllTwilioPhonesSuccess(state, action as AppAction<Array<string>>);
    case actionTypes.TWILIO_GET_ALL_TWILIO_PHONES_FAIL:
      return getAllTwilioPhonesFail(state, action as AppAction<Array<string>>);
    case actionTypes.TWILIO_SET_ALL_TWILIO_PHONES:
      return setAllTwilioPhones(state, action as AppAction<Array<string>>);

    case actionTypes.TWILIO_GET_TWILIO_ASSIGNED_AGENTS_START:
      return getTwilioAssignedAgentsStart(state, action as AppAction<Array<PhoneGroup>>);
    case actionTypes.TWILIO_GET_TWILIO_ASSIGNED_AGENTS_SUCCESS:
      return getTwilioAssignedAgentsSuccess(state, action as AppAction<Array<PhoneGroup>>);
    case actionTypes.TWILIO_GET_TWILIO_ASSIGNED_AGENTS_FAIL:
      return getTwilioAssignedAgentsFail(state, action as AppAction<Array<PhoneGroup>>);
    case actionTypes.TWILIO_SET_TWILIO_ASSIGNED_AGENTS:
      return setTwilioAssignedAgents(state, action as AppAction<Array<PhoneGroup>>);

    case actionTypes.TWILIO_SAVE_TWILIO_ASSIGNED_AGENTS_START:
      return saveTwilioAssignedAgentsStart(state, action as AppAction<boolean>);
    case actionTypes.TWILIO_SAVE_TWILIO_ASSIGNED_AGENTS_SUCCESS:
      return saveTwilioAssignedAgentsSuccess(state, action as AppAction<boolean>);
    case actionTypes.TWILIO_SAVE_TWILIO_ASSIGNED_AGENTS_FAIL:
      return saveTwilioAssignedAgentsFail(state, action as AppAction<boolean>);

    default:
      return state;
  }
};

export default reducer;
